import React, { useState } from 'react';
import { Button, Checkbox, LoadingSpinner, QueryErrorAlert, Tooltip, PageHeader, SearchBar } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { DataTable } from './DataTable';
import { Pagination } from '@/shared/components';
import { Filters } from './Filters';
import { FilterIcon, FilterXIcon, RefreshCwIcon } from 'lucide-react';
import { useDashboardSearch } from '../../hooks/useDashboardSearch';

interface DashboardSearchProps {
    title: string;
    subtitle: string;
    placeholder?: string;
}

export const DashboardSearch: React.FC<DashboardSearchProps> = ({
    title,
    subtitle,
    placeholder = 'Search for a Quote or an Agreement...',
}) => {
    const {
        items,
        meta,
        thirdPartyStatus,
        isLoading,
        error,
        resetSearch,
        refreshSearch,
        isFetching,
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        sortConfig,
        handleSort,
        pagination,
        handlePageChange,
        handlePerPageChange,
        filters,
        handleFiltersChange,
        clearFilters,
        includeThirdParty,
        handleIncludeThirdPartyChange,
    } = useDashboardSearch();

    const isSearchPending = searchTerm !== debouncedSearchTerm && searchTerm.length > 0;
    const [showFilters, setShowFilters] = useState(false);

    const handleShowHideFilters = () => {
        setShowFilters(!showFilters);
    };

    return (
        <div className="mt-4 space-y-4">
            <PageHeader title={title} subtitle={subtitle} />
            <div className="flex flex-wrap justify-start content-start">
                <div className="relative grow max-w-md">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeholder={placeholder}
                    />
                    {/* Show search state indicator */}
                    {isSearchPending && (
                        <LoadingSpinner
                            size="sm"
                            decorative
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        />
                    )}
                    {isFetching && !isSearchPending && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-pulse rounded-full h-4 w-4 bg-mp-primary"></div>
                        </div>
                    )}
                </div>

                <PermissionGuard permission={{ resource: 'third_party_agreement', action: 'browse' }}>
                    <div className="ml-3 flex items-center">
                        <Checkbox
                            label="Include Third Party agreements"
                            checked={includeThirdParty}
                            onChange={handleIncludeThirdPartyChange}
                        />
                    </div>
                </PermissionGuard>

                {/* Search controls */}
                {searchTerm && (
                    <Button onClick={resetSearch} variant="clear" size="xs">
                        Clear Search
                    </Button>
                )}

                <div className="ml-auto">
                    {/* Show / hide filters */}
                    <Tooltip content={`${showFilters ? 'Hide Filters' : 'Show Filters'}`}>
                        <Button onClick={handleShowHideFilters} variant="clear" size="xs">
                            {showFilters ? <FilterXIcon size={22} /> : <FilterIcon size={22} />}
                        </Button>
                    </Tooltip>
                    <Tooltip content={isFetching ? 'Refreshing...' : 'Refresh'}>
                        <Button
                            onClick={refreshSearch}
                            variant="clear"
                            size="xs"
                            ariaLabel={isFetching ? 'Refreshing...' : 'Refresh'}
                        >
                            <RefreshCwIcon className={isFetching ? 'animate-spin' : ''} size={22} />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            {showFilters && (
                <Filters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={clearFilters}
                />
            )}
            {/* Error state */}
            {error && (
                <QueryErrorAlert
                    message={`Failed to load records: ${error.message}`}
                    onRetry={refreshSearch}
                    isRetrying={isFetching}
                />
            )}
            {/* Loading state */}
            {isLoading && !items.length && (
                <LoadingSpinner size="md" className="py-12" message="Loading quotes and agreements..." />
            )}
            {/* Dashboard Item Table */}
            {!isLoading || items.length > 0 ? (
                <div className="space-y-4">
                    {thirdPartyStatus === 'unavailable' && (
                        <p role="status" className="text-sm text-mp-muted">
                            Third Party results unavailable
                        </p>
                    )}
                    <DataTable data={items} sortConfig={sortConfig} onSort={handleSort} />

                    {/* Pagination */}
                    {meta && meta.last_page > 1 && (
                        <Pagination
                            currentPage={meta.current_page}
                            lastPage={meta.last_page}
                            total={meta.total}
                            perPage={meta.per_page}
                            from={meta.from}
                            to={meta.to}
                            onPageChange={handlePageChange}
                            onPerPageChange={handlePerPageChange}
                        />
                    )}
                </div>
            ) : null}{' '}
            {/* Debug Info (remove in production) */}
            {process.env.NODE_ENV === 'development' && (
                <details className="text-xs text-mp-muted border border-mp-border rounded p-2">
                    <summary>Debug: API Parameters & Search State</summary>
                    <pre className="mt-2 whitespace-pre-wrap">
                        {JSON.stringify(
                            {
                                searchTerm,
                                debouncedSearchTerm,
                                isSearchPending,
                                isFetching,
                                dashboardItemsCount: items.length,
                                error: error?.message,
                                sortConfig,
                                pagination,
                                meta,
                            },
                            null,
                            2
                        )}
                    </pre>
                </details>
            )}
        </div>
    );
};
